import isArray from 'lodash/isArray';
import tail from 'lodash/tail';

import {
  FunnelQueryResponse,
  isFunnelStepArray,
  isSegmentedData,
  SegmentedFunnelResults,
  UnsegmentedFunnelResult,
} from 'common/report/data/funnels/api-types';
import {SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {BasicFunnelStep, BucketData, ChartType, FunnelBookmark, FunnelStep} from 'common/report/models/funnels/types';
import {BucketDefinition} from 'common/report/models/funnels/bar-chart/types';
import {
  Bar,
  BarPattern,
  BarSegment,
  Segment as BarVizSegment,
  SegmentWithChildren,
  SegmentWithoutChildren,
} from 'common/charts/visualization/bar-viz/types';
import {
  getBucketData,
  getBucketFromBookmarkFormat,
  getFunnelStep,
  percentOfTotal,
} from 'common/report/models/funnels/bar-chart/utils';
import {formatDuration, getPrecision} from 'common/util/date';
import * as colors from 'common/util/colors';
import {OVERALL_PROP, OVERALL_PROP_NAME} from 'common/report/models/funnels/constants';
import {ResourceType, SavedLegend, ValueRepresentationType} from 'common/report/constants';
import {FrequencyViewType} from 'common/report/models/funnels/frequency-chart/types';
import {addConversionRateToFunnelStep} from 'common/report/data/funnels/utils';
import {
  conversionMode,
  getBucketLabel as frequencyBucketLabeler,
} from 'common/report/models/funnels/frequency-chart/utils';
import {
  getBucketAccessor as ttcBucketAccessor,
  getBucketLabel as ttcBucketLabeler,
  getTTCShiftedEventIndex,
} from 'common/report/models/funnels/time-to-convert/utils';
import {isCohortSegment, isEventSegment, isUserSegment, Segment} from 'common/widgets/query-block/segment-menu/types';
import {renameProperty} from 'common/report/util/properties';

import {convertOldSavedSegments} from './colors';

export interface BucketValueInfo {
  count: number;
  percentage: number;
  bucketIdx: number;
  isOutsideBound: boolean;
}

type SegmentKeyAndLabel = {id: string; label: string};

export interface FunnelSegmentExtras {
  segmentPath: SegmentKeyAndLabel[];
}

export function getStepIndexFromBookmark(bookmark: FunnelBookmark): number | null {
  switch (bookmark.chart_type) {
    case ChartType.Frequency:
      return bookmark.eventIndex || 0;
    case ChartType.TimeToConvert:
      return getTTCShiftedEventIndex(bookmark.eventIndex);
    default:
      throw new Error(`Unsupported chart type for bookmark step index`);
  }
}

function bookmarkIsRelative(bookmark: FunnelBookmark): boolean {
  switch (bookmark.chart_type) {
    case ChartType.Frequency:
      return (
        bookmark.frequencyType === FrequencyViewType.ConversionRate ||
        bookmark.frequencyValueRepresentation === ValueRepresentationType.Relative
      );
    case ChartType.TimeToConvert:
      return bookmark.ttcValueRepresentation === ValueRepresentationType.Relative;
    default:
      return false;
  }
}

function getBucketDefinitionAndAccessorKeysFromBookmark(
  bookmark: FunnelBookmark,
): {bucketAccessor: keyof FunnelStep; bucketDefinition: BucketDefinition} {
  if (bookmark.chart_type === ChartType.TimeToConvert) {
    return {
      bucketAccessor: ttcBucketAccessor(getStepIndexFromBookmark(bookmark)),
      bucketDefinition: getBucketFromBookmarkFormat(bookmark.time_buckets),
    };
  }
  return {
    bucketAccessor: conversionMode(bookmark.frequencyType).bucketAccessor,
    bucketDefinition: getBucketFromBookmarkFormat(bookmark.frequency_buckets),
  };
}

export function bookmarkHasTimeComparison(bookmark: FunnelBookmark): boolean {
  return !!bookmark.time_comparison;
}

function getBucketDataFromSegment(segment: FunnelStep[], stepIndex, bucketAccessor): BucketData {
  const step = getFunnelStep(segment, stepIndex);
  return getBucketData(step, bucketAccessor);
}

function getValuesForBucket(bucketData, index, showCountAsPercentage) {
  const buckets = bucketData.buckets;
  let count = buckets.length > 0 ? buckets[index] : 0;
  //index=-1 for overall lower and index=buckets.length for overall higher
  if (index < 0 || index >= buckets.length) {
    count = index >= buckets.length ? bucketData.higher : bucketData.lower;
  }

  const percentage = percentOfTotal(bucketData, count, showCountAsPercentage);

  return {count, percentage};
}

/**
 *  Generates BarViz segments from funnel results and a bookmark
 * @param segments - Tree of segmented funnel steps
 * @param compareSegments - Tree of the same shape as the segments with compare data at each leaf corresponding
 * @param selectedSegmentKeys - In order of tree traversal, so that the first element corresponds to the first depth of the tree
 * e.g. for keys: [ [NYC, Seattle], [OSX, Windows] ], segments should look like:
 * {
 *   NYC: {
 *     OSX: <step>,
 *     Windows: <step>
 *   }
 *   Seattle: {
 *     OSX: <step>,
 *     Windows: <step>
 *   }
 * }
 *
 * @param bookmark - The query used to generate the funnel results
 * @param segmentPath - current keys used to traverse the result tree to get to this node
 */
export function generateBarsFromFunnelSegmentTree({
  segments,
  compareSegments,
  selectedSegmentKeys,
  bookmark,
  segmentPath,
}: {
  segments: SegmentedFunnelResults;
  compareSegments?: SegmentedFunnelResults;
  selectedSegmentKeys: SegmentSection[];
  bookmark: FunnelBookmark;
  segmentPath: SegmentKeyAndLabel[];
}): BarVizSegment[] {
  // Always grab the first segment group which matches this depth of segments
  const segmentsToSearchFor = selectedSegmentKeys[0]?.items;
  if (!segmentsToSearchFor) {
    return [];
  }

  const segmentChildren: BarVizSegment[] = [];

  for (let i = 0; i < segmentsToSearchFor.length; i++) {
    const segmentKey = segmentsToSearchFor[i];
    const resultingSegment = segments[segmentKey.id];

    if (isArray(resultingSegment)) {
      // arrays are termination steps, return this path key
      const distributionSegments = generateLeafSegment({
        segment: resultingSegment,
        compareSegment: compareSegments ? (compareSegments[segmentKey.id] as FunnelStep[]) : null,
        color: segmentKey.color,
        bookmark,
        segmentLabel: segmentKey.label,
        segmentPath: segmentPath.concat(segmentKey),
      });

      if (distributionSegments.length) {
        const segmentParent: SegmentWithChildren = {
          children: distributionSegments,
          label: segmentKey.label,
          segmentName: selectedSegmentKeys[0].label,
        };

        segmentChildren.push(segmentParent);
      }
    } else if (resultingSegment) {
      const subChildren = generateBarsFromFunnelSegmentTree({
        segments: resultingSegment,
        compareSegments: compareSegments ? (compareSegments[segmentKey.id] as SegmentedFunnelResults) : null,
        selectedSegmentKeys: tail(selectedSegmentKeys),
        bookmark,
        segmentPath: segmentPath.concat(segmentKey),
      });

      if (subChildren.length) {
        segmentChildren.push({
          segmentName: selectedSegmentKeys[0].label,
          label: segmentKey.id,
          children: subChildren,
        });
      }
    }
  }

  return segmentChildren;
}

export function generateLeafSegment({
  segment,
  compareSegment,
  color,
  bookmark,
  segmentLabel,
  segmentPath,
}: {
  segment: FunnelStep[];
  compareSegment?: FunnelStep[];
  color: string;
  bookmark: FunnelBookmark;
  segmentLabel: string;
  segmentPath: SegmentKeyAndLabel[];
}): Array<SegmentWithoutChildren<FunnelSegmentExtras>> {
  const isRelative = bookmarkIsRelative(bookmark);
  const showCountAsPercentage =
    bookmark.chart_type === ChartType.Frequency && bookmark.frequencyType === FrequencyViewType.ConversionRate;

  const getBucketLabel = bookmark.chart_type === ChartType.Frequency ? frequencyBucketLabeler : ttcBucketLabeler;
  const stepIndex = getStepIndexFromBookmark(bookmark);
  const {bucketDefinition, bucketAccessor} = getBucketDefinitionAndAccessorKeysFromBookmark(bookmark);

  const bucketData = getBucketDataFromSegment(segment, stepIndex, bucketAccessor);

  const buckets = getDistributionForSegment({
    bucket: bucketData,
    compareBucket: compareSegment ? getBucketDataFromSegment(compareSegment, stepIndex, bucketAccessor) : null,
    bucketDefinition,
    getBucketLabel,
    showCountAsPercentage,
    isRelative,
  });

  let compareBuckets = [];
  if (compareSegment) {
    const compareBucketData = getBucketDataFromSegment(compareSegment, stepIndex, bucketAccessor);

    compareBuckets = getDistributionForSegment({
      bucket: compareBucketData,
      compareBucket: bucketData,
      bucketDefinition,
      getBucketLabel,
      showCountAsPercentage,
      isRelative,
    });
  }

  return buckets.map(({value, label, extras}, idx) => {
    const bars: Array<Bar<FunnelSegmentExtras>> = [
      {
        segments: [
          {
            value,
            label: isRelative ? `${value}%` : String(value),
            color,
            extras: {
              ...extras,
              segmentPath,
            },
          },
        ],
        extras: {
          segmentPath,
        },
      },
    ];

    if (compareSegment) {
      const {value: compareValue, extras} = compareBuckets[idx];
      bars.push({
        segments: [
          {
            pattern: BarPattern.Striped,
            value: compareValue,
            label: isRelative ? `${compareValue}%` : String(compareValue),
            color,
            extras,
          },
        ],
        extras: {
          segmentPath,
        },
      });
    }

    return {
      segmentName: `Frequency`,
      label,
      bars,
      extras: {
        parentLabel: segmentLabel,
        segmentPath,
      },
    };
  });
}

function createCollapsedBuckets({
  bucket,
  compareBucket,
  showCountAsPercentage,
}: {
  bucket: BucketData;
  compareBucket?: BucketData;
  showCountAsPercentage: boolean;
}): Array<{count: number; percentage: number; bucketIdx: number}> {
  const {buckets} = bucket;
  const collapsedBuckets: Array<{count: number; percentage: number; bucketIdx: number}> = [];
  let canAddLeadingZeroBucket = true;

  for (let bucketIdx = 0; bucketIdx < buckets.length; bucketIdx++) {
    const {count: bucketCount, percentage} = getValuesForBucket(bucket, bucketIdx, showCountAsPercentage);

    if (canAddLeadingZeroBucket && bucketCount === 0) {
      collapsedBuckets.push({count: 0, percentage: 0, bucketIdx});
      canAddLeadingZeroBucket = false;
    } else if (bucketCount !== 0) {
      collapsedBuckets.push({count: bucketCount, percentage, bucketIdx});
      canAddLeadingZeroBucket = true;
    } else if (compareBucket) {
      const previousCompareBucketHadData =
        bucketIdx > 0 && getValuesForBucket(compareBucket, bucketIdx - 1, showCountAsPercentage).count !== 0;
      const compareBucketHasData = getValuesForBucket(compareBucket, bucketIdx, showCountAsPercentage).count !== 0;

      // Ensure we don't skip this zero bucket IF the compare bucket had data in the previous step OR this step
      // This guarantees we don't collapse buckets that have data
      if (compareBucketHasData || previousCompareBucketHadData) {
        collapsedBuckets.push({count: 0, percentage: 0, bucketIdx});
      }
    }
  }

  return collapsedBuckets;
}

/** Gets the distribution for a specific bucket, while collapsing adjacent buckets
 * e.g. if the data for a segment is
 * 1: 0
 * 2: 12
 * 3 0
 * Then it will return values and labels for [1: 0, 2: 12, 3: 0]
 */
export function getDistributionForSegment({
  bucket,
  compareBucket,
  bucketDefinition,
  getBucketLabel,
  showCountAsPercentage,
  isRelative,
}: {
  bucket: BucketData;
  compareBucket?: BucketData;
  bucketDefinition: BucketDefinition;
  getBucketLabel: (bucketStart: number, bucketEnd?: number, precision?: number) => string;
  showCountAsPercentage: boolean;
  isRelative: boolean;
}): Array<BarSegment<BucketValueInfo>> {
  const {bucketSize, min: bucketMin, max: bucketMax} = bucketDefinition;

  const collapsedBuckets = createCollapsedBuckets({
    bucket,
    compareBucket,
    showCountAsPercentage,
  });

  const bucketSegments: Array<BarSegment<BucketValueInfo>> = [];

  for (let idx = 0; idx < collapsedBuckets.length; idx++) {
    const bucketStart = bucketMin + collapsedBuckets[idx].bucketIdx * bucketSize;

    let bucketEnd;
    // Last bucket's end should always be the max bucket or the "overflow" from the next section
    if (idx === collapsedBuckets.length - 1) {
      bucketEnd = Math.max(bucketMax, bucketMin + (collapsedBuckets[idx].bucketIdx + 1) * bucketSize);
    } else {
      bucketEnd = bucketMin + collapsedBuckets[idx + 1].bucketIdx * bucketSize;
    }

    const endTime = bucketStart + bucketSize * bucketSize;
    const endUnit = formatDuration(endTime * 1000, 0).unit;
    const precision = getPrecision(endUnit, bucketSize * 1000);

    const bucketLabel = getBucketLabel(bucketStart, bucketEnd, precision);

    bucketSegments.push({
      value: isRelative ? collapsedBuckets[idx].percentage : collapsedBuckets[idx].count,
      label: bucketLabel,
      extras: {
        count: collapsedBuckets[idx].count,
        percentage: collapsedBuckets[idx].percentage,
        bucketIdx: collapsedBuckets[idx].bucketIdx,
        isOutsideBound: false,
      },
    });
  }

  // Add higher and lower values if needed
  if (bucket.lower || compareBucket?.lower) {
    const {count, percentage} = getValuesForBucket(bucket, -1, showCountAsPercentage);
    const bucketLabel = getBucketLabel(bucketMin);
    bucketSegments.unshift({
      value: isRelative ? percentage : count,
      label: `< ${bucketLabel}`,
      extras: {
        count,
        percentage,
        bucketIdx: -1,
        isOutsideBound: true,
      },
    });
  }

  if (bucket.higher || compareBucket?.higher) {
    const {count, percentage} = getValuesForBucket(bucket, bucket.buckets.length, showCountAsPercentage);
    const bucketLabel = getBucketLabel(bucketMax);
    bucketSegments.push({
      value: isRelative ? percentage : count,
      label: `> ${bucketLabel}`,
      extras: {
        count,
        percentage,
        bucketIdx: bucket.buckets.length,
        isOutsideBound: true,
      },
    });
  }

  if (
    bucketSegments.every((bucket) => bucket.value === 0) &&
    compareBucket?.buckets.every((value) => value === 0) &&
    compareBucket?.lower === 0 &&
    compareBucket?.higher === 0
  ) {
    return [];
  }

  return bucketSegments;
}

export function generateLegendSections(
  funnelsResponse: FunnelQueryResponse,
  bookmark: FunnelBookmark,
): {sections: SegmentSection[]; savedLegend: SavedLegend[]} {
  const segmentData = funnelsResponse.data[funnelsResponse.meta.dates[0]];

  if (isSegmentedData(segmentData)) {
    const segmentNames = bookmark.group_by;

    // TODO(reviewer): we naively pull the segment names from the data. Should we have the api return it?
    // (the returns flattened segments, but maybe we should modify it to return the segments for each breakdown)
    // this doesn't work great if we have segments that have little overlap (i.e. country = US and cities = singapore, paris, etc)
    // TODO we should probably do a pass on the tree to find the largest segments or something similar, otherwise the
    // report doesn't really return the most interesting segments (just alphabetical will miss a lot of the good stuff for high cardinality)
    const segmentItems: SegmentSection[] = [];

    let curr: SegmentedFunnelResults | Array<BasicFunnelStep | FunnelStep> = segmentData;
    let i = 0;

    let savedLegend = null;
    if (bookmark?.savedLegend) {
      savedLegend = bookmark.savedLegend;
    } else if (bookmark?.selectedSegments?.length) {
      savedLegend = convertOldSavedSegments(bookmark.selectedSegments);
    }

    while (curr) {
      const segment = segmentNames[i];
      const keys = Object.keys(curr).filter((s) => s !== `$overall`);
      const next = curr[keys[0]];
      const isFinalSegment = isFunnelStepArray(next);

      segmentItems.push({
        label: getSegmentDisplayName(segment, bookmark.data_group_id),
        items: keys.map((key) => ({
          id: key,
          label: key,
        })),
      });

      if (!isFinalSegment) {
        curr = next;
        i++;
      } else {
        break;
      }
    }

    return {
      // We reverse segment items so that the most segmented legend is the one at the top (which has colors)
      sections: segmentItems.reverse(),
      savedLegend,
    };
  }

  return {
    sections: [
      {
        items: [
          {
            id: OVERALL_PROP,
            label: OVERALL_PROP_NAME,
            color: colors.getChartSegmentColorCombo(0).primary, // Overall is always the first primary color
          },
        ],
      },
    ],
    savedLegend: null,
  };
}

export function getBarChartDataFromRawFunnel({
  funnelsResponse,
  bookmark,
  selectedSections,
}: {
  funnelsResponse: FunnelQueryResponse;
  selectedSections: SegmentSection[];
  bookmark: FunnelBookmark;
}): Array<BarVizSegment<FunnelSegmentExtras>> {
  const date = funnelsResponse.meta.dates[0];
  const segmentData = funnelsResponse.data[date];

  if (isSegmentedData(segmentData)) {
    return generateBarsFromFunnelSegmentTree({
      segments: segmentData,
      compareSegments: bookmarkHasTimeComparison(bookmark)
        ? (Object.values(funnelsResponse.compare_data)[0] as SegmentedFunnelResults)
        : null,
      selectedSegmentKeys: selectedSections,
      bookmark,
      segmentPath: [],
    });
  }

  return generateLeafSegment({
    segment: segmentData.steps,
    compareSegment: bookmarkHasTimeComparison(bookmark)
      ? (Object.values(funnelsResponse.compare_data)[0] as UnsegmentedFunnelResult)?.steps
      : null,
    color: colors.getChartSegmentColorCombo(0).primary,
    bookmark,
    segmentLabel: OVERALL_PROP_NAME,
    segmentPath: [{id: OVERALL_PROP, label: OVERALL_PROP_NAME}],
  });
}

/**
 * Modifies the funnels reponse in place, adding conversion rates to all of the leaf-level steps
 * TODO(sam) - eventually refactor `addConversionToFunnelStep` to not modify in place, and instead
 * return a new funnel step typed with conversion rate
 * It was left as is for now to not have to refactor more funnels code than necessary
 */
export function addConversionRateToFunnel(
  funnelsResponse: FunnelQueryResponse,
  bookmark: FunnelBookmark,
): FunnelQueryResponse {
  const date = funnelsResponse.meta.dates[0];

  const segmentData = funnelsResponse.data[date];

  const q: Array<SegmentedFunnelResults | UnsegmentedFunnelResult> = [segmentData];

  if (bookmarkHasTimeComparison(bookmark)) {
    q.push(Object.values(funnelsResponse.compare_data)[0]);
  }

  while (q.length) {
    const curr = q.pop();

    if (isArray(curr)) {
      // leaf node with steps, add conversion to them
      curr.map(addConversionRateToFunnelStep);
    } else {
      const segments = Object.keys(curr).map((key) => curr[key]);

      q.push(...segments);
    }
  }

  return funnelsResponse;
}

export function getSegmentDisplayName(segment: Segment, dataGroupId: string): string {
  if (isCohortSegment(segment)) {
    const cohortSegment = segment[0];
    return renameProperty(cohortSegment[ResourceType.Cohort].name, ResourceType.Cohort, {
      dataGroupId,
    });
  } else if (isEventSegment(segment)) {
    return renameProperty(segment[ResourceType.Event].propertyName, ResourceType.Event, {
      dataGroupId,
      customProperty: segment[ResourceType.Event].customProperty,
      propertyObjectKey: segment[ResourceType.Event].propertyObjectKey,
    });
  } else if (isUserSegment(segment)) {
    return renameProperty(segment[ResourceType.User].propertyName, ResourceType.User, {
      dataGroupId,
      customProperty: segment[ResourceType.User].customProperty,
      propertyObjectKey: segment[ResourceType.User].propertyObjectKey,
    });
  } else {
    return renameProperty(segment[ResourceType.Other].label, ResourceType.Other, {
      dataGroupId,
      customProperty: segment[ResourceType.Other].customProperty,
      propertyObjectKey: segment[ResourceType.Other].propertyObjectKey,
    });
  }
}
